package dev.lingvo.site.components.editor

import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.site.api.Session
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.ProjectRouteContext
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException

/** One presentation read; it never republishes the verified route or grants editor authority. */
internal class EditorMetadataRead(
    private val projectSlug: String,
    expectedProject: ProjectDetailDto?,
    private val generationIsCurrent: () -> Boolean,
) {
    private val session = Session.requestIdentity
    private val path = window.location.pathname
    private val cachedRoute = ProjectRouteContext.current(projectSlug)
    val verifiedRoute = liveProjectRoute(projectSlug)
    private val expectedProject = expectedProject ?: verifiedRoute?.project
    private val claim = ProjectActionClaim.capture(verifiedRoute, Session.organizations, ProjectPrivilege.READ)

    fun isCurrent(): Boolean = generationIsCurrent() &&
        Session.requestIdentity == session && window.location.pathname == path &&
        if (verifiedRoute != null) {
            claim?.remainsAuthorized(liveProjectRoute(projectSlug), Session.organizations) == true
        } else {
            // Preserve independently mounted/pre-read callers, but never downgrade a retired
            // verified route into that fallback.
            cachedRoute == null
        }

    fun requireCurrent() {
        if (!isCurrent()) throw CancellationException("The editor metadata read was retired.")
    }

    suspend fun <T> request(transport: suspend () -> T): T {
        requireCurrent()
        val result = try {
            if (claim == null) transport() else requestProjectAction(
                claim = claim,
                currentRoute = { liveProjectRoute(projectSlug) },
                currentMemberships = { Session.organizations },
                transport = transport,
            )
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            requireCurrent()
            throw failure
        }
        requireCurrent()
        return result
    }

    fun validate(project: ProjectDetailDto): ProjectDetailDto {
        requireCurrent()
        check(project.slug == projectSlug) { "The project response did not match this editor link." }
        expectedProject?.let { expected ->
            check(project.id == expected.id && project.orgId == expected.orgId &&
                (project.orgSlug.isBlank() || expected.orgSlug.isBlank() || project.orgSlug == expected.orgSlug)) {
                "The project response did not match this editor workspace."
            }
        }
        check(project.files.all { it.projectId == project.id }) {
            "The project files did not match this editor link."
        }
        return verifiedRoute?.workspace?.let { workspace ->
            checkNotNull(workspace.normalizeProject(project)) { "The project response did not match this editor workspace." }
        } ?: project
    }
}
